import { Link } from "@tanstack/react-router";
import type { ColDef } from "ag-grid-community";
import React from "react";

import { AgGridReact } from "@/components/grid/AgGrid";
import {
  DataList,
  DataListItem,
  DataListLabel,
  DataListValue,
} from "@/components/ui/data-list";
import { Tab, TabPanel, Tabs, TabsList } from "@/components/ui/tabs";
import { format } from "@/lib/utils";
import type { WorkspaceAccession } from "../types";
import { workspaceGridTheme } from "../utils/grid";
import { WorkflowStepHeader } from "./WorkflowStep";
import { useWorkspace } from "./WorkspaceProvider";

export function WorkspaceOverview() {
  const { testRun, samples, accessions, testSpec } = useWorkspace();
  return (
    <>
      <WorkflowStepHeader title="Overview" />
      <div className="p-6">
        <DataList
          orientation="horizontal"
          labelWidth={100}
          // className="border rounded-lg p-4"
        >
          <DataListItem>
            <DataListLabel>Test Type</DataListLabel>
            <DataListValue>{testSpec?.name}</DataListValue>
          </DataListItem>
          <DataListItem>
            <DataListLabel>Run ID</DataListLabel>
            <DataListValue>{testRun.id}</DataListValue>
          </DataListItem>
          {/* <DataListItem>
            <DataListLabel>Status</DataListLabel>
            <DataListValue>
              <Badge variant="soft" color="blue">
                {workflow.status || "PENDING"}
              </Badge>
            </DataListValue>
          </DataListItem> */}
          <DataListItem>
            <DataListLabel>Accessions</DataListLabel>
            <DataListValue>{accessions.length} accessions</DataListValue>
          </DataListItem>
          <DataListItem>
            <DataListLabel>Samples</DataListLabel>
            <DataListValue>{samples.length} samples</DataListValue>
          </DataListItem>
        </DataList>
      </div>
      <Tabs
        defaultSelectedKey="accessions"
        className="flex-1 flex flex-col"
        // variant="pills"
      >
        <TabsList className="justify-start px-6 border-b">
          <Tab id="accessions">Accessions</Tab>
          <Tab id="samples">Samples</Tab>
        </TabsList>
        <TabPanel id="accessions" className="flex-1 mt-0 ag-theme-blis">
          <AccessionGrid />
        </TabPanel>
        <TabPanel id="samples" className="flex-1 mt-0 ag-theme-blis">
          <SampleGrid />
        </TabPanel>
        {/* <TabPanel id="materials">
          <MaterialGrid />
        </TabPanel> */}
      </Tabs>
    </>
  );
}

function AccessionGrid() {
  const { accessions } = useWorkspace();

  const columns = React.useMemo(
    () =>
      [
        {
          field: "id",
          headerName: "Accession",
          cellClass: "tabular-nums",
          minWidth: 120,
          maxWidth: 150,
          cellRenderer: (params) => {
            return (
              <Link to="/accessions/$id" params={{ id: params.data.id }}>
                {params.data.id}
              </Link>
            );
          },
        },
        {
          field: "subject.caseId",
          headerName: "Subject",
          cellRenderer: (params) => {
            return (
              <Link to="/subjects/$id" params={{ id: params.data.subject.id }}>
                {params.data.subject.caseId}
              </Link>
            );
          },
        },
        {
          field: "org.name",
          headerName: "Organization",
        },
        {
          field: "receivedAt",
          headerName: "Received",
          cellDataType: "dateTime",
          valueFormatter: (params) => {
            return format(params.value);
          },
        },
        {
          field: "_count.samples",
          headerName: "Samples",
          minWidth: 100,
          maxWidth: 100,
        },
      ] as ColDef<WorkspaceAccession>[],
    [],
  );

  const defaultColDef = React.useMemo(
    () =>
      ({
        suppressHeaderMenuButton: true,
        flex: 1,
      }) as ColDef,
    [],
  );

  return (
    <AgGridReact
      columnDefs={columns}
      rowData={accessions}
      theme={workspaceGridTheme}
      defaultColDef={defaultColDef}
    />
  );
}

function SampleGrid() {
  const { samples, accessions } = useWorkspace();

  const samplesWithAccessions = React.useMemo(() => {
    return samples.map((sample) => ({
      ...sample,
      accession: accessions.find((a) => a.id === sample.accessionId),
    }));
  }, [samples, accessions]);

  const columns = React.useMemo(
    () =>
      [
        {
          field: "displayId",
          headerName: "Sample",
        },
        {
          field: "accessionId",
          headerName: "Accession",
          cellClass: "tabular-nums",
          minWidth: 120,
          maxWidth: 150,
          cellRenderer: (params) => {
            return (
              <Link
                to="/accessions/$id"
                params={{ id: params.data.accessionId }}
              >
                {params.data.accessionId}
              </Link>
            );
          },
        },
        {
          field: "tube.name",
          headerName: "Container",
        },
        {
          field: "accession.subject.caseId",
          headerName: "Subject",
          cellRenderer: (params) => {
            return (
              <Link
                to="/subjects/$id"
                params={{ id: params.data.accession.subject.id }}
              >
                {params.data.accession.subject.caseId}
              </Link>
            );
          },
        },
        {
          field: "accession.org.name",
          headerName: "Organization",
        },
        {
          field: "accession.receivedAt",
          headerName: "Received",
          cellDataType: "dateTime",
          valueFormatter: (params) => {
            return format(params.value);
          },
        },
      ] as ColDef<(typeof samplesWithAccessions)[number]>[],
    [],
  );

  const defaultColDef = React.useMemo(
    () =>
      ({
        suppressHeaderMenuButton: true,
        flex: 1,
      }) as ColDef,
    [],
  );

  return (
    <AgGridReact
      columnDefs={columns}
      rowData={samplesWithAccessions}
      theme={workspaceGridTheme}
      defaultColDef={defaultColDef}
    />
  );
}
